import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import LanguageSwitcher from '../../components/common/LanguageSwitcher';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Skeleton,
  TextField,
  InputAdornment
} from '@mui/material';
import { Person as PersonIcon, Event as EventIcon, LocationOn as LocationIcon, Search as SearchIcon } from '@mui/icons-material';

const GuestView = () => {
  const { t } = useTranslation();
  const { weddingId } = useParams();
  const [wedding, setWedding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchWedding = async () => {
      if (!weddingId) {
        setError(t('common.notFound'));
        setLoading(false);
        return;
      }

      try {
        console.log('Fetching wedding with ID:', weddingId);
        const weddingDoc = await getDoc(doc(db, 'weddings', weddingId));
        
        if (!weddingDoc.exists()) {
          console.log('Wedding not found');
          setError(t('common.notFound'));
          setLoading(false);
          return;
        }
        
        const weddingData = weddingDoc.data();
        console.log('Wedding data:', weddingData);
        setWedding({ id: weddingDoc.id, ...weddingData });
      } catch (err) {
        console.error('Error fetching wedding:', err);
        setError(t('common.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchWedding();
  }, [weddingId, t]);

  // Filter tables based on search term
  const filteredTables = wedding?.tables?.map(table => ({
    ...table,
    members: table.members?.filter(member => 
      member.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(table => table.members?.length > 0) || [];

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Paper sx={{ p: 4, mt: 4 }}>
          <Skeleton variant="text" height={60} sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            {[1, 2, 3].map((n) => (
              <Grid item xs={12} sm={6} md={4} key={n}>
                <Skeleton variant="rectangular" height={200} />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>
    );
  }

  if (error || !wedding) {
    return (
      <Container maxWidth="sm">
        <Alert severity="error" sx={{ mt: 4 }}>
          {error || t('common.notFound')}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Paper sx={{ p: 4, mt: 4 }}>
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          {wedding.name}
        </Typography>

        {/* Language Switcher */}
        <LanguageSwitcher />

        {/* Wedding Details */}
        <Box sx={{ my: 3 }}>
          {wedding.date && (
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <EventIcon sx={{ mr: 1 }} />
              {wedding.date.toDate().toLocaleDateString()}
            </Typography>
          )}
          {wedding.location && (
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
              <LocationIcon sx={{ mr: 1 }} />
              {wedding.location}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Search Box */}
        <Box sx={{ mb: 4 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t('qrCode.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Tables */}
        <Box sx={{ mt: 4 }}>
          {wedding.tables?.length > 0 ? (
            searchTerm ? (
              // Show filtered results when searching
              filteredTables.length > 0 ? (
                <Grid container spacing={3}>
                  {filteredTables.map((table, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {t('tables.tableNumber')} {table.tableNumber}
                          </Typography>
                          <Divider sx={{ my: 2 }} />
                          <List dense>
                            {table.members?.map((member, memberIndex) => (
                              <ListItem key={memberIndex}>
                                <ListItemIcon>
                                  <PersonIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={member}
                                  primaryTypographyProps={{
                                    style: {
                                      fontWeight: member.toLowerCase().includes(searchTerm.toLowerCase()) ? 'bold' : 'normal'
                                    }
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Alert severity="info">
                  {t('qrCode.notFound')}
                </Alert>
              )
            ) : (
              // Show all tables when not searching
              <Grid container spacing={3}>
                {wedding.tables.map((table, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {t('tables.tableNumber')} {table.tableNumber}
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        {table.members?.length > 0 ? (
                          <List dense>
                            {table.members.map((member, memberIndex) => (
                              <ListItem key={memberIndex}>
                                <ListItemIcon>
                                  <PersonIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary={member} />
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Typography color="text.secondary">
                            {t('tables.noMembers')}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )
          ) : (
            <Alert severity="info">
              {t('tables.noTables')}
            </Alert>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default GuestView; 